import React from "react";
import { AlertTriangle, AlertOctagon, EyeOff, FileX2, Compass } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import { SectionContainer, SectionLabel, SectionHeading, SectionLead } from "./SectionPrimitives";

const ITEMS = [
  {
    icon: FileX2,
    code: "ERR-001",
    title: "Unvollständige Fahrzeugdaten",
    desc: "Pflichtfelder leer, Ausstattung fehlt, Bilder nicht zugeordnet — Google diskualifiziert Ihre Anzeigen.",
  },
  {
    icon: AlertOctagon,
    code: "ERR-014",
    title: "Fehlende Pflichtattribute",
    desc: "VIN, Mileage, Body-Style, Drivetrain. Ohne diese Felder kein Vehicle-Ad-Listing.",
  },
  {
    icon: EyeOff,
    code: "ERR-027",
    title: "Schwache Fahrzeugseiten",
    desc: "Dünner Content, keine Schema-Daten, keine internen Verlinkungen. SEO-Wert: nahe Null.",
  },
  {
    icon: AlertTriangle,
    code: "ERR-031",
    title: "Fehleranfällige Feeds",
    desc: "Stündliche Disapprovals, manuelle Re-Submits, keine Validierungs-Logik. Operatives Risiko.",
  },
  {
    icon: Compass,
    code: "ERR-046",
    title: "Verlorene Sichtbarkeit",
    desc: "Reichweite versickert, qualifizierte Klicks fehlen. Vertriebsfunnel reißt im obersten Drittel ab.",
  },
];

export default function Problem() {
  return (
    <SectionContainer id="fehlerkatalog" className="bg-white">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionLabel number="/ 02" label="Das Problem" />
          <SectionHeading>Daten ohne Struktur sind kein Vertriebskanal.</SectionHeading>
          <SectionLead>
            Die meisten Händlerbestände bestehen aus inkonsistenten DMS-Exporten,
            ungepflegten Bildern und schwachen Fahrzeugseiten. Daraus entsteht
            kein verlässlicher Performance-Funnel.
          </SectionLead>

          <Reveal delay={0.08} className="mt-10">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
              <p className="label-mono text-slate-500">Beobachtete Verluste</p>
              <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Stat value="−42%" label="Sichtbarkeit" />
                <Stat value="−27%" label="Klick-Rate" />
                <Stat value="3.6×" label="Manueller Aufwand" />
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <StaggerGroup className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white">
              <p className="label-mono text-slate-500">Fehlerkatalog · Auszug</p>
              <p className="font-mono text-[11px] text-slate-400">5 / 47 typische Befunde</p>
            </div>
            <ul className="divide-y divide-slate-200">
              {ITEMS.map((it) => {
                const Icon = it.icon;
                return (
                  <StaggerItem key={it.code}>
                    <li className="flex items-start gap-4 px-5 py-5 transition-colors hover:bg-white">
                      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white">
                        <Icon className="size-4 text-amber-600" />
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">{it.code}</span>
                          <h3 className="font-display font-semibold text-slate-900">{it.title}</h3>
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{it.desc}</p>
                      </div>
                    </li>
                  </StaggerItem>
                );
              })}
            </ul>
          </StaggerGroup>
        </div>
      </div>
    </SectionContainer>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="font-display text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
      <p className="label-mono text-slate-500 mt-1">{label}</p>
    </div>
  );
}
